/**
 * Material Design 3 (M3) Theme for Malaysia (MYS) region
 * Based on https://m3.material.io/develop
 *
 * M3 key concepts applied:
 * - Dynamic color with tonal palettes
 * - Updated typography scale
 * - Rounded shape system
 * - Elevation with tonal surface colors
 */

import { createTheme } from '@mui/material'

// M3 Color Tokens - Primary tonal palette (Green-based for MYS brand from Figma)
const m3Colors = {
    // Primary
    primary: '#4B662C',
    onPrimary: '#FFFFFF',
    primaryContainer: '#D2EC9F',
    onPrimaryContainer: '#394D12',

    // Secondary
    secondary: '#414942',
    onSecondary: '#FFFFFF',
    secondaryContainer: '#D2EC9F',
    onSecondaryContainer: '#394D12',

    // Tertiary
    tertiary: '#7D5260',
    onTertiary: '#FFFFFF',
    tertiaryContainer: '#FFD8E4',
    onTertiaryContainer: '#31111D',

    // Error
    error: '#B3261E',
    onError: '#FFFFFF',
    errorContainer: '#F9DEDC',
    onErrorContainer: '#410E0B',

    // Surface
    surface: '#FFFFFF',
    onSurface: '#171D1B',
    surfaceVariant: '#E7E0EC',
    onSurfaceVariant: '#414942',
    surfaceContainer: '#E9EFEC',
    surfaceContainerLow: '#EFF5F1',

    // Outline
    outline: '#79747E',
    outlineVariant: '#C0C9BF',

    // Background
    background: '#FFFFFF',
    onBackground: '#171D1B',

    // Inverse
    inverseSurface: '#313033',
    inverseOnSurface: '#F4EFF4',
    inversePrimary: '#D2EC9F',
}

// M3 Typography Scale
const m3Typography = {
    fontFamily: '"Inter", "Roboto", sans-serif',
    // Display
    displayLarge: { fontSize: '3.5625rem', lineHeight: '4rem', fontWeight: 400, letterSpacing: '-0.25px' },
    displayMedium: { fontSize: '2.8125rem', lineHeight: '3.25rem', fontWeight: 400, letterSpacing: '0px' },
    displaySmall: { fontSize: '2.25rem', lineHeight: '2.75rem', fontWeight: 400, letterSpacing: '0px' },
    // Headline
    headlineLarge: { fontSize: '2rem', lineHeight: '2.5rem', fontWeight: 400, letterSpacing: '0px' },
    headlineMedium: { fontSize: '1.75rem', lineHeight: '2.25rem', fontWeight: 400, letterSpacing: '0px' },
    headlineSmall: { fontSize: '1.5rem', lineHeight: '2rem', fontWeight: 400, letterSpacing: '0px' },
    // Title
    titleLarge: { fontSize: '1.375rem', lineHeight: '1.75rem', fontWeight: 600, letterSpacing: '0px' },
    titleMedium: { fontSize: '16px', lineHeight: '24px', fontWeight: 600, letterSpacing: '0.15px' },
    titleSmall: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500, letterSpacing: '0.1px' },
    // Body
    bodyLarge: { fontSize: '16px', lineHeight: '24px', fontWeight: 400, letterSpacing: '0.5px' },
    bodyMedium: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 400, letterSpacing: '0.25px' },
    bodySmall: { fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400, letterSpacing: '0.4px' },
    // Label
    labelLarge: { fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500, letterSpacing: '0.1px' },
    labelMedium: { fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 500, letterSpacing: '0.5px' },
    labelSmall: { fontSize: '0.6875rem', lineHeight: '1rem', fontWeight: 500, letterSpacing: '0.5px' },
}

// M3 Shape Scale
const m3Shape = {
    none: 0,
    extraSmall: 4,
    small: 8,
    medium: 12,
    large: 16,
    extraLarge: 28,
    full: 9999,
}

export const m3Theme = createTheme({
    typography: {
        fontFamily: m3Typography.fontFamily,
        fontWeightMedium: 500,
        fontWeightRegular: 400,
        fontWeightLight: 300,
        fontWeightBold: 700,
        h1: {
            ...m3Typography.headlineLarge,
        },
        h2: {
            ...m3Typography.headlineMedium,
        },
        h3: {
            ...m3Typography.headlineSmall,
        },
        h4: {
            ...m3Typography.titleLarge,
        },
        h5: {
            ...m3Typography.titleMedium,
        },
        h6: {
            ...m3Typography.titleSmall,
        },
        subtitle1: {
            ...m3Typography.bodyLarge,
        },
        subtitle2: {
            ...m3Typography.bodyMedium,
        },
        body1: {
            ...m3Typography.bodyLarge,
        },
        body2: {
            ...m3Typography.bodyMedium,
        },
        button: {
            ...m3Typography.labelLarge,
            textTransform: 'none' as const,
        },
        caption: {
            ...m3Typography.bodySmall,
        },
        overline: {
            ...m3Typography.labelSmall,
            textTransform: 'uppercase' as const,
        },
    },
    palette: {
        primary: {
            main: m3Colors.primary,
            light: m3Colors.primaryContainer,
            dark: m3Colors.onPrimaryContainer,
            contrastText: m3Colors.onPrimary,
        },
        secondary: {
            main: m3Colors.secondary,
            light: m3Colors.secondaryContainer,
            dark: m3Colors.onSecondaryContainer,
            contrastText: m3Colors.onSecondary,
        },
        error: {
            main: m3Colors.error,
            light: m3Colors.errorContainer,
            dark: m3Colors.onErrorContainer,
            contrastText: m3Colors.onError,
        },
        background: {
            default: m3Colors.background,
            paper: m3Colors.surface,
        },
        text: {
            primary: m3Colors.onSurface,
            secondary: m3Colors.onSurfaceVariant,
            disabled: m3Colors.outline,
        },
        divider: m3Colors.outlineVariant,
        action: {
            active: m3Colors.primary,
            focus: m3Colors.primary,
            disabledBackground: m3Colors.surfaceVariant,
        },
    },
    shape: {
        borderRadius: m3Shape.medium,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '24px',
                    letterSpacing: '0.5px',
                    color: m3Colors.onSurface,
                },
                '*': {
                    fontFamily: 'inherit',
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    color: m3Colors.onSurface,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontFamily: '"Inter", "Roboto", sans-serif !important',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0.5px',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: '"Inter", "Roboto", sans-serif',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: m3Shape.full,
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '10px 24px',
                    letterSpacing: '0.1px',
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                    },
                },
                outlined: {
                    borderColor: m3Colors.outline,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: m3Shape.medium,
                    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: m3Shape.extraSmall,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: m3Shape.small,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: m3Shape.extraLarge,
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    borderRadius: m3Shape.large,
                    boxShadow: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px rgba(0, 0, 0, 0.3)',
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: m3Colors.surface,
                    color: m3Colors.onSurface,
                    boxShadow: 'none',
                },
            },
        },
        MuiBottomNavigation: {
            styleOverrides: {
                root: {
                    backgroundColor: m3Colors.surface,
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-thumb': {
                        borderRadius: m3Shape.full,
                    },
                },
            },
        },
    },
})

// Export M3 color tokens for direct use in components
export { m3Colors, m3Shape, m3Typography }
